import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import {
  Camera, CheckCircle, Package, TrendingUp, BarChart3,
  DollarSign, Clock, Users, Activity, Download
} from 'lucide-react';

const COLORS = {
  amber: '#F59E0B', blue: '#3B82F6', green: '#10B981', red: '#EF4444',
  purple: '#8B5CF6', cyan: '#06B6D4', gray: '#64748B',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1E293B', border: '1px solid #334155', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 24px rgba(0,0,0,0.4)', fontSize: 12
    }}>
      <p style={{ color: '#94A3B8', marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || COLORS.amber, fontWeight: 600 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('es-ES') : p.value}
        </p>
      ))}
    </div>
  );
};

export default function ProjectDashboard({ projectId, project }) {
  const [photoActivity, setPhotoActivity] = useState([]);
  const [taskDist, setTaskDist] = useState(null);
  const [equipSummary, setEquipSummary] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    const params = { project_id: projectId };
    Promise.all([
      api.get('/analytics/photo-activity', { params: { ...params, days: 30 } }),
      api.get('/analytics/task-distribution', { params }),
      api.get('/analytics/equipment-summary', { params }),
      api.get('/analytics/productivity', { params: { ...params, days: 30 } }),
      api.get('/analytics/milestones', { params }),
    ].map(p => p.catch(() => ({ data: null }))))
      .then(([phR, tR, eR, pR, mR]) => {
        setPhotoActivity(phR.data || []);
        setTaskDist(tR.data);
        setEquipSummary(eR.data);
        setProductivity(pR.data);
        setMilestones(mR.data);
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) return <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>;

  // Task data
  const taskStatusData = (taskDist?.byStatus || []).map(s => ({
    name: statusLabel(s.status), value: parseInt(s.count), status: s.status
  }));
  const categoryData = (taskDist?.byCategory || []).map(c => ({
    name: c.code || c.category || '?',
    total: parseInt(c.total), completed: parseInt(c.completed),
  }));
  const completionRate = (taskDist?.completionRate || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    completadas: parseInt(d.completed),
  }));

  // Photo data
  const photoChartData = (photoActivity || []).map(d => ({
    date: new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
    total: parseInt(d.total),
  }));
  const totalPhotos = photoChartData.reduce((s, d) => s + d.total, 0);

  // Equipment data
  const equipByStatus = (equipSummary?.byStatus || []).map(s => ({
    name: statusLabel(s.status), value: parseInt(s.count),
  }));
  const totalEquip = equipByStatus.reduce((s, d) => s + d.value, 0);

  // Productivity
  const photosByUser = (productivity?.photosByUser || []).slice(0, 5);
  const tasksByUser = (productivity?.tasksByUser || []).slice(0, 5);

  // Milestones
  const ms = milestones?.summary || {};
  const totalBillable = parseFloat(ms.total_billable) || 0;
  const totalReceived = parseFloat(ms.total_received) || 0;
  const pendingInvoice = parseFloat(ms.pending_invoice) || 0;

  const downloadPdf = async () => {
    try {
      const response = await api.get(`/reports/project/${projectId}/pdf`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([response.data]));
      link.download = `informe-${project?.code || 'proyecto'}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
    } catch (e) { console.error(e); }
  };

  const downloadExcel = async () => {
    try {
      const response = await api.get(`/reports/project/${projectId}/excel`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([response.data]));
      link.download = `reporte-${project?.code || 'proyecto'}-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="project-dashboard">
      {/* Quick stats */}
      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginBottom: 20 }}>
        <MiniKpi icon={<Camera size={18} />} label="Fotos (30d)" value={totalPhotos} color="purple" />
        <MiniKpi icon={<CheckCircle size={18} />} label="Tareas OK"
          value={`${taskStatusData.find(t => t.status === 'completed')?.value || 0}/${taskStatusData.reduce((s, t) => s + t.value, 0)}`} color="green" />
        <MiniKpi icon={<Package size={18} />} label="Equipos" value={totalEquip} color="blue" />
        <MiniKpi icon={<DollarSign size={18} />} label="Cobrado"
          value={`${formatCurrency(totalReceived)}€`} color="green" />
        <MiniKpi icon={<DollarSign size={18} />} label="Pendiente"
          value={`${formatCurrency(pendingInvoice)}€`} color="amber" />
      </div>

      {/* Download buttons */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <button className="btn btn-sm btn-secondary" onClick={downloadPdf}>
          <Download size={14} /> PDF
        </button>
        <button className="btn btn-sm btn-secondary" onClick={downloadExcel}>
          <Download size={14} /> Excel
        </button>
      </div>

      {/* Charts row 1 */}
      <div className="dashboard-grid">
        {/* Task pie */}
        <div className="card">
          <div className="card-header"><h3><Activity size={16} /> Tareas por estado</h3></div>
          <div className="card-body" style={{ height: 220 }}>
            {taskStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={taskStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                    paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {taskStatusData.map((d, i) => (
                      <Cell key={i} fill={statusColor(d.status)} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="empty-text">Sin datos</p>}
          </div>
        </div>

        {/* Photo activity */}
        <div className="card">
          <div className="card-header"><h3><Camera size={16} /> Fotos (30 días)</h3></div>
          <div className="card-body" style={{ height: 220 }}>
            {photoChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={photoChartData}>
                  <defs>
                    <linearGradient id="projPhotoGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.amber} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.amber} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 9 }} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="total" stroke={COLORS.amber} fill="url(#projPhotoGrad)" name="Fotos" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="empty-text">Sin datos</p>}
          </div>
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="dashboard-grid" style={{ marginTop: 16 }}>
        {/* Tasks by category */}
        <div className="card">
          <div className="card-header"><h3><BarChart3 size={16} /> Tareas por categoría</h3></div>
          <div className="card-body" style={{ height: 220 }}>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                  <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 9 }} />
                  <YAxis tick={{ fill: '#64748B', fontSize: 9 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="completed" name="Completadas" fill={COLORS.green} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="total" name="Total" fill={COLORS.blue} opacity={0.3} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="empty-text">Sin datos</p>}
          </div>
        </div>

        {/* Equipment status */}
        <div className="card">
          <div className="card-header"><h3><Package size={16} /> Equipos por estado</h3></div>
          <div className="card-body" style={{ height: 220 }}>
            {equipByStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={equipByStatus} cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                    paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {equipByStatus.map((_, i) => (
                      <Cell key={i} fill={[COLORS.green, COLORS.amber, COLORS.blue, COLORS.gray, COLORS.red][i % 5]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="empty-text">Sin datos</p>}
          </div>
        </div>
      </div>

      {/* Charts row 3: Productivity */}
      {(photosByUser.length > 0 || tasksByUser.length > 0) && (
        <div className="dashboard-grid" style={{ marginTop: 16 }}>
          {photosByUser.length > 0 && (
            <div className="card">
              <div className="card-header"><h3><Users size={16} /> Fotos por usuario</h3></div>
              <div className="card-body" style={{ height: Math.max(160, photosByUser.length * 30) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={photosByUser} layout="vertical" barSize={14}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                    <XAxis type="number" tick={{ fill: '#64748B', fontSize: 9 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#94A3B8', fontSize: 10 }} width={100} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="photos" name="Fotos" fill={COLORS.purple} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {tasksByUser.length > 0 && (
            <div className="card">
              <div className="card-header"><h3><Users size={16} /> Tareas por usuario</h3></div>
              <div className="card-body" style={{ height: Math.max(160, tasksByUser.length * 30) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tasksByUser} layout="vertical" barSize={14}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                    <XAxis type="number" tick={{ fill: '#64748B', fontSize: 9 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#94A3B8', fontSize: 10 }} width={100} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="tasks_completed" name="Completadas" fill={COLORS.green} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completion trend */}
      {completionRate.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header"><h3><TrendingUp size={16} /> Completación diaria (30d)</h3></div>
          <div className="card-body" style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={completionRate}>
                <defs>
                  <linearGradient id="compGradProj" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="date" tick={{ fill: '#64748B', fontSize: 9 }} />
                <YAxis tick={{ fill: '#64748B', fontSize: 9 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="completadas" stroke={COLORS.green} fill="url(#compGradProj)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ icon, label, value, color }) {
  return (
    <div className="kpi-card kpi-card-sm">
      <div className={`kpi-icon ${color}`}>{icon}</div>
      <div className="kpi-data">
        <span className="kpi-value kpi-value-sm">{value}</span>
        <span className="kpi-label">{label}</span>
      </div>
    </div>
  );
}

function statusLabel(s) {
  return { completed: 'Completadas', in_progress: 'En progreso', pending: 'Pendientes',
    blocked: 'Bloqueadas', in_warehouse: 'Almacén', in_site: 'En obra',
    installed: 'Instalado', operational: 'Operativo' }[s] || s || '?';
}

function statusColor(s) {
  return { completed: COLORS.green, in_progress: COLORS.blue, pending: COLORS.gray,
    blocked: COLORS.red }[s] || COLORS.gray;
}

function formatCurrency(n) {
  return Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 });
}
